// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Types
import { Theme } from '../../../../../../../../types/theme';

// Hooks
import { useBreakpoint } from '../../../../../../../../hooks/useBreakpoint';

// Plugins
import {
  booleanToEnabled,
  booleanToEnableStatus,
} from '../../../../../../../../plugins/crusher';

// Components
import { Col } from '../../../../../../../../layout/elements/Col';
import { EnableItem } from '../../../../../../../../components/EnableItem';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../../../layout/elements/Row';

// Component
import { SensorsProps } from './Sensors.types';
import {
  EnableItemsContainer,
  LabelsContainer,
  LastEnableItemsContainer,
  LastSeparator,
  SensorsContainer,
  Separator,
} from './Sensors.style';

function Sensors({
  className,
  unitData,
  edgeStatus,
  testId,
}: SensorsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  return (
    <div className={className} data-testid={testId}>
      <Paragraph
        size={
          isXs
            ? ParagraphSize.sm
            : ParagraphSize.lg
        }
        weight={ParagraphWeight.bold}
      >
        {t('view.crusher.maintenance.setup.sensors.title')}
      </Paragraph>
      <Row style={{ rowGap: 16 }}>
        <Col cols={24} xl={8}>
          <SensorsContainer>
            <LabelsContainer>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                color={
                  theme.color.crusherDashboard.maintenance.setup.sensors.descriptionTextColor
                }
              >
                {t('common.status')}
              </Paragraph>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                color={
                  theme.color.crusherDashboard.maintenance.setup.sensors.descriptionTextColor
                }
              >
                {t('common.sensor')}
              </Paragraph>
            </LabelsContainer>
            <Separator />
            <EnableItemsContainer>
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.lubeUnitDescription')}
                enabled={booleanToEnabled(unitData?.lubeFlowSwitchEnable)}
                status={booleanToEnableStatus(
                  unitData?.lubeFlowSwitchEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.lubeLevelDescription')}
                enabled={booleanToEnabled(unitData?.lubeLevelEnable)}
                status={booleanToEnableStatus(
                  unitData?.lubeLevelEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t(
                  'view.crusher.maintenance.setup.sensors.lubeSupplyDescription',
                )}
                enabled={booleanToEnabled(unitData?.lubeSupplyTempEnable)}
                status={booleanToEnableStatus(
                  unitData?.lubeSupplyTempEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t(
                  'view.crusher.maintenance.setup.sensors.eccentricBusingDescription',
                )}
                enabled={booleanToEnabled(unitData?.eccentricBushingTempEnable)}
                status={booleanToEnableStatus(
                  unitData?.eccentricBushingTempEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
            </EnableItemsContainer>
          </SensorsContainer>
        </Col>
        <Col cols={24} xl={8}>
          <SensorsContainer>
            <LabelsContainer>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                color={
                  theme.color.crusherDashboard.maintenance.setup.sensors.descriptionTextColor
                }
              >
                {t('common.status')}
              </Paragraph>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                color={
                  theme.color.crusherDashboard.maintenance.setup.sensors.descriptionTextColor
                }
              >
                {t('common.sensor')}
              </Paragraph>
            </LabelsContainer>
            <Separator />
            <EnableItemsContainer>
              <EnableItem
                description={t(
                  'view.crusher.maintenance.setup.sensors.cavityLevelDescription',
                )}
                enabled={booleanToEnabled(unitData?.cavityLevelSensorEnable)}
                status={booleanToEnableStatus(
                  unitData?.cavityLevelSensorEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t(
                  'view.crusher.maintenance.setup.sensors.crusherSpeedDescription',
                )}
                enabled={booleanToEnabled(unitData?.crusherSpeedMonitorEnable)}
                status={booleanToEnableStatus(
                  unitData?.crusherSpeedMonitorEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.beltSlipDescription')}
                enabled={booleanToEnabled(unitData?.beltSlipMonitorEnable)}
                status={booleanToEnableStatus(
                  unitData?.beltSlipMonitorEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t(
                  'view.crusher.maintenance.setup.sensors.countershaftBushingDescription',
                )}
                enabled={booleanToEnabled(unitData?.counterShaftBushingTempEnable)}
                status={booleanToEnableStatus(
                  unitData?.counterShaftBushingTempEnable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
            </EnableItemsContainer>
          </SensorsContainer>
        </Col>
        <Col cols={24} xl={8}>
          <SensorsContainer>
            <LabelsContainer>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                color={
                  theme.color.crusherDashboard.maintenance.setup.sensors.descriptionTextColor
                }
              >
                {t('common.status')}
              </Paragraph>
              <Paragraph
                size={
                  isXs
                    ? ParagraphSize.xs
                    : ParagraphSize.sm
                }
                color={
                  theme.color.crusherDashboard.maintenance.setup.sensors.descriptionTextColor
                }
              >
                {t('common.sensor')}
              </Paragraph>
            </LabelsContainer>
            <LastSeparator />
            <LastEnableItemsContainer>
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.vibrationSensor1')}
                enabled={booleanToEnabled(unitData?.vibrationSensor1Enable)}
                status={booleanToEnableStatus(
                  unitData?.vibrationSensor1Enable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.vibrationSensor2')}
                enabled={booleanToEnabled(unitData?.vibrationSensor2Enable)}
                status={booleanToEnableStatus(
                  unitData?.vibrationSensor2Enable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.vibrationSensor3')}
                enabled={booleanToEnabled(unitData?.vibrationSensor3Enable)}
                status={booleanToEnableStatus(
                  unitData?.vibrationSensor3Enable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
              <EnableItem
                description={t('view.crusher.maintenance.setup.sensors.vibrationSensor4')}
                enabled={booleanToEnabled(unitData?.vibrationSensor4Enable)}
                status={booleanToEnableStatus(
                  unitData?.vibrationSensor4Enable,
                  edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                  unitData?.timestamp ? unitData?.timestamp : null,
                )}
              />
            </LastEnableItemsContainer>
          </SensorsContainer>
        </Col>
      </Row>
    </div>
  );
}

export { Sensors };
